import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Button } from "@/components/ui/button";
import {
  FormFieldsEditor,
  filterEmptyFields,
  type FormFieldMap,
} from "@/components/settings/form-fields-editor";
import type { IPortcoForm } from "@/types/data-models";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import posthog from "posthog-js";

export default function PortcoUpdatesSettings() {
  const { workspace, isLoading, activeWorkspaceId, updateWorkspaceMutation } =
    useWorkspaceSettings();
  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();
  const workspaceId = urlWorkspaceId || activeWorkspaceId;

  const [fields, setFields] = useState<IPortcoForm>({});

  useEffect(() => {
    if (workspace) {
      setFields(workspace.portco_form || {});
    }
  }, [workspace]);

  const isDirty =
    JSON.stringify(
      filterEmptyFields(fields as FormFieldMap) as IPortcoForm,
    ) !== JSON.stringify(workspace?.portco_form || {});

  const handleSave = () => {
    const filtered = filterEmptyFields(fields as FormFieldMap) as IPortcoForm;
    const toastId = toast.loading("Saving...");
    updateWorkspaceMutation.mutate(
      { portco_form: filtered },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          toast.success("Portco form saved");
          posthog.capture("settings_portco_form_updated", {
            workspace_id: activeWorkspaceId,
            field_count: Object.keys(filtered).length,
          });
        },
        onError: () => {
          toast.dismiss(toastId);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
      <h1 className="text-lg font-semibold text-foreground">Portco Updates</h1>
      <p className="mt-0.5 text-sm text-muted-foreground">
        The form your portfolio companies fill out on request.
      </p>

      {/* Status-scope callout */}
      <div className="mt-6 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
        <p className="text-muted-foreground">
          This form only applies to deals with status{" "}
          <span className="font-medium text-foreground">
            "Portfolio Companies"
          </span>
          . Change a deal's status from the Pipeline to make this form available
          to them.
        </p>
        <Link
          to={`/${workspaceId}/deals`}
          className="mt-1 inline-block text-xs font-medium text-foreground underline-offset-4 hover:underline"
        >
          Open Pipeline →
        </Link>
      </div>

      <section className="mt-8">
        <h2 className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
          Portfolio Company Update Form
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Configure fields for portfolio companies to provide updates.
        </p>
        <FormFieldsEditor
          fields={fields as FormFieldMap}
          onChange={(next) => setFields(next as IPortcoForm)}
          addLabel="Add Field"
        />
      </section>

    </div>
    <div className="sticky bottom-0 border-t border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex max-w-2xl justify-end px-6 py-3">
        <Button
          onClick={handleSave}
          disabled={updateWorkspaceMutation.isPending || !isDirty}
        >
          {updateWorkspaceMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
    </>
  );
}
